import React, { useCallback, useEffect, useState } from "react";
import { Brain, Loader2, RefreshCw, ScrollText } from "lucide-react";
import { toast } from "sonner";
import AiPageShell from "../../components/ai/AiPageShell";
import { AiMarkdown } from "../../components/ai/AiMarkdown";
import { akilIslemLoglari, akilOgrenmeOzet, akilOgrenmeSync } from "../../lib/akilApi";

function formatDate(value) {
    if (!value) return "—";
    try {
        return new Date(value).toLocaleString("tr-TR");
    } catch {
        return value;
    }
}

export default function AkilLogPage() {
    const [loading, setLoading] = useState(true);
    const [ogrenmeLoading, setOgrenmeLoading] = useState(true);
    const [rows, setRows] = useState([]);
    const [count, setCount] = useState(0);
    const [ogrenme, setOgrenme] = useState(null);
    const [syncBusy, setSyncBusy] = useState(false);

    const loadOgrenme = useCallback(async () => {
        setOgrenmeLoading(true);
        try {
            const data = await akilOgrenmeOzet(30);
            setOgrenme(data);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Öğrenme özeti yüklenemedi.");
        } finally {
            setOgrenmeLoading(false);
        }
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const data = await akilIslemLoglari({ page_size: 50, ordering: "-olusturma_tarihi" });
            setRows(data.results || data || []);
            setCount(data.count ?? (data.results || data || []).length);
        } catch (err) {
            toast.error(err.response?.data?.detail || err.message || "Loglar yüklenemedi.");
        } finally {
            setLoading(false);
        }
    }, []);

    const refreshAll = useCallback(async () => {
        await Promise.all([load(), loadOgrenme()]);
    }, [load, loadOgrenme]);

    const runSync = async () => {
        setSyncBusy(true);
        try {
            const data = await akilOgrenmeSync(100);
            toast.success(`Bilgi bankası güncellendi — ${data.toplam_bilgi || 0} parça`);
            await loadOgrenme();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Senkron başarısız.");
        } finally {
            setSyncBusy(false);
        }
    };

    useEffect(() => {
        refreshAll();
    }, [refreshAll]);

    return (
        <AiPageShell
            title="Akıl İşlem Logları"
            subtitle="Kim, ne sordu, ne hesapladı — audit ve kullanım istatistikleri."
            actions={
                <div className="flex gap-2">
                    <button
                        type="button"
                        onClick={runSync}
                        disabled={syncBusy}
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-[#D4AF37]/30 text-xs text-[#F3E5AB] hover:bg-[#D4AF37]/10 disabled:opacity-50"
                    >
                        {syncBusy ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                            <Brain className="h-3.5 w-3.5" />
                        )}
                        Bilgi bankasını öğret
                    </button>
                    <button
                        type="button"
                        onClick={refreshAll}
                        disabled={loading || ogrenmeLoading}
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-white/15 text-xs text-slate-300 hover:text-white disabled:opacity-50"
                    >
                        {loading || ogrenmeLoading ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                            <RefreshCw className="h-3.5 w-3.5" />
                        )}
                        Yenile
                    </button>
                </div>
            }
        >
            <div className="rounded-xl border border-[#D4AF37]/20 bg-[#D4AF37]/5 p-4 mb-5">
                <div className="flex items-start gap-3">
                    <Brain className="h-5 w-5 text-[#D4AF37] shrink-0 mt-0.5" />
                    <div className="min-w-0">
                        <h2 className="text-sm font-semibold text-[#F3E5AB]">Akıl bilgi bankası</h2>
                        {ogrenmeLoading ? (
                            <p className="text-xs text-slate-400 mt-1">Yükleniyor…</p>
                        ) : ogrenme?.metin ? (
                            <div className="text-xs text-slate-300 mt-2 prose-invert max-w-none">
                                <AiMarkdown text={ogrenme.metin} />
                            </div>
                        ) : (
                            <p className="text-xs text-slate-400 mt-1">
                                Bilgi bankası boş — &quot;Bilgi bankasını öğret&quot; ile tohum + log senkronu yapın.
                            </p>
                        )}
                    </div>
                </div>
            </div>

            <div className="rounded-xl border border-white/10 overflow-hidden">
                <div className="px-4 py-3 border-b border-white/10 bg-white/[0.03] flex items-center gap-2 text-sm text-slate-400">
                    <ScrollText className="h-4 w-4 text-[#D4AF37]" />
                    Toplam {count} kayıt (son 50)
                </div>
                {loading && !rows.length ? (
                    <div className="py-16 flex justify-center text-slate-500 gap-2">
                        <Loader2 className="h-5 w-5 animate-spin" /> Yükleniyor…
                    </div>
                ) : !rows.length ? (
                    <p className="py-12 text-center text-slate-500 text-sm">Henüz log kaydı yok.</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-white/10">
                                    <th className="px-4 py-3">Tarih</th>
                                    <th className="px-4 py-3">Kullanıcı</th>
                                    <th className="px-4 py-3">Intent</th>
                                    <th className="px-4 py-3">Mesaj</th>
                                    <th className="px-4 py-3">Sonuç</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r) => (
                                    <tr
                                        key={r.id}
                                        className="border-b border-white/5 hover:bg-white/[0.02]"
                                    >
                                        <td className="px-4 py-3 text-slate-400 whitespace-nowrap">
                                            {formatDate(r.olusturma_tarihi)}
                                        </td>
                                        <td className="px-4 py-3 text-slate-300">
                                            {r.kullanici_display || r.kullanici || "—"}
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className="text-[#F3E5AB]">{r.intent || r.islem_tipi}</span>
                                        </td>
                                        <td className="px-4 py-3 text-slate-400 max-w-md truncate" title={r.mesaj}>
                                            {r.mesaj || "—"}
                                        </td>
                                        <td className="px-4 py-3">
                                            <span
                                                className={`text-xs px-2 py-0.5 rounded-full ${
                                                    r.sonuc === "hata"
                                                        ? "bg-red-500/20 text-red-300"
                                                        : r.sonuc === "onay_bekliyor"
                                                          ? "bg-amber-500/20 text-amber-200"
                                                          : "bg-emerald-500/15 text-emerald-300"
                                                }`}
                                            >
                                                {r.sonuc || "—"}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </AiPageShell>
    );
}
